import React, {useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {skipToken} from "@reduxjs/toolkit/query";

import {
    CitySlug,
    getCityMapRegionParams,
    OsmMapRegionParams,
    resolveMapRegionParamsFromCityQuery
} from "../../../../config/cities";
import {useAppSelector} from "../../../../utils/hooks/store_hooks";
import {mapPathnameToOfferType} from "../../common/app/routing/offer_type";
import {useChartsRegionId} from "../../features/analytical_platform/hooks/use_charts_region_id";
import OpenStreetMapBrowserOnly from "../OpenStreetMapBrowserOnly";
import {ICbiMapOffer, useGetOffersListMapQuery} from "./api/map_api";
import {OfferType} from "./mocks/singleOfferMock";
import {selectChartsSelectedRegion, selectMapCityPocCityQuery} from "./redux/map_city_poc_slice";
import {MARKER_CLUSTER_DISABLE_ZOOM} from "./marker_cluster_constants";
import {OFFER_LIST_MARKER_SIZE, offerListMarkerIconUri} from "./offer_list_marker_icon";
import {buildOfferListMarkerTooltipHtml} from "./offer_list_marker_tooltip";
/** One step wider than previous tweak (Leaflet `fitBounds` + optional offset below). */
const OFFER_MAP_INITIAL_ZOOM = 10;

interface IProps {
    city: CitySlug | null;
    onMarkerClick: (id: string) => void;
    withoutOffers?: boolean;
    activeOfferId?: string | null;
    /** Leaflet wrapper height; defaults to `100%` so the map fills card/flex parents (legacy default was 400px in OpenStreetMap). */
    mapHeight?: string;
    /** Pixels: Offer panel overlaps map from left → pan correction (`DashboardMapChartCard`). */
    focusOfferLeftInsetPx?: number;
    /**
     * Listing maps draw district outlines but should not mirror the Added-page hover (`region` + metric).
     * `undefined`: show polygon hover only when `districtValuesBySlug` has data (district thematic map).
     */
    districtPolygonTooltip?: boolean;
    /** When true, only the top-level city outline is shown — no district subdivisions. */
    cityOutlineOnly?: boolean;
}

export const LeafletMap = React.memo(
    ({
        city,
        onMarkerClick,
        withoutOffers,
        mapHeight,
        activeOfferId,
        focusOfferLeftInsetPx,
        districtPolygonTooltip = false,
        cityOutlineOnly = false
    }: IProps) => {
        const {t} = useTranslation();
        const {pathname} = useLocation();
        const pocCityQuery = useAppSelector(selectMapCityPocCityQuery);
        const chartsSelectedRegion = useAppSelector(selectChartsSelectedRegion);
        const {regionId: routeRegionId} = useChartsRegionId();
        const cbiMapRegionId = chartsSelectedRegion?.regionId ?? routeRegionId ?? null;
        const mapRegionParams: OsmMapRegionParams =
            chartsSelectedRegion != null
                ? {
                      regionsMapSlug: chartsSelectedRegion.regionsMapSlug,
                      regionsMapParentId: chartsSelectedRegion.regionsMapParentId,
                      regionsMapRegionId: chartsSelectedRegion.regionsMapRegionId
                  }
                : pocCityQuery != null
                  ? resolveMapRegionParamsFromCityQuery(pocCityQuery)
                  : city != null
                    ? getCityMapRegionParams(city)
                    : {};
        const {regionsMapSlug, regionsMapParentId, regionsMapRegionId} = mapRegionParams;
        const offerType = mapPathnameToOfferType(pathname);
        const offersQueryArgs = useMemo(
            () => (withoutOffers || cbiMapRegionId == null ? skipToken : {region_id: cbiMapRegionId, offerType}),
            [withoutOffers, cbiMapRegionId, offerType]
        );

        const offersRes = useGetOffersListMapQuery(offersQueryArgs);

        const onMarkerClickRef = useRef(onMarkerClick);
        onMarkerClickRef.current = onMarkerClick;

        const offersRequestKey =
            offersQueryArgs === skipToken ? null : `${offersQueryArgs.region_id}\0${offersQueryArgs.offerType}`;
        const lastFetchedOffersKeyRef = useRef<string | null>(null);
        const offersArgsPending = offersRequestKey != null && lastFetchedOffersKeyRef.current !== offersRequestKey;

        useEffect(() => {
            if (offersRequestKey == null) {
                lastFetchedOffersKeyRef.current = null;
                return;
            }
            if (!offersRes.isFetching && offersRes.isSuccess) {
                lastFetchedOffersKeyRef.current = offersRequestKey;
            }
        }, [offersRequestKey, offersRes.isFetching, offersRes.isSuccess]);

        const markersLoading =
            !withoutOffers &&
            cbiMapRegionId != null &&
            (offersRes.isLoading || (offersRes.isFetching && offersArgsPending));

        const markers = useMemo(() => {
            if (markersLoading) {
                return [];
            }

            return (
                offersRes.data?.map((offer: ICbiMapOffer) => {
                    const isActive = activeOfferId != null && offer.id === activeOfferId;
                    const markerUrl = isActive
                        ? offerListMarkerIconUri.active
                        : offer.type === OfferType.FLAT
                          ? offerListMarkerIconUri[OfferType.FLAT]
                          : offerListMarkerIconUri[OfferType.HOUSE];
                    const countLabel =
                        offer.type === OfferType.HOUSE
                            ? t("offer_box.map_tooltip_houses_count", {
                                  count: offer.stats.properties_count_for_sale
                              })
                            : t("offer_box.map_tooltip_flats_count", {
                                  count: offer.stats.properties_count_for_sale
                              });

                    return {
                        id: offer.id,
                        coords: [offer.geo_point.coordinates[1], offer.geo_point.coordinates[0]] as [number, number],
                        offerListType: offer.type,
                        onClick: () => onMarkerClickRef.current(offer.id),
                        popup: isActive
                            ? undefined
                            : buildOfferListMarkerTooltipHtml({
                                  name: offer.name,
                                  vendorName: offer.vendor?.name ?? "",
                                  countLabel
                              }),
                        icon: {
                            url: markerUrl,
                            sizes: OFFER_LIST_MARKER_SIZE
                        }
                    };
                }) ?? []
            );
        }, [activeOfferId, markersLoading, offersRes.data, t]);

        return (
            <OpenStreetMapBrowserOnly
                regionsMapSlug={regionsMapSlug}
                regionsMapParentId={cityOutlineOnly ? undefined : regionsMapParentId}
                regionsMapRegionId={regionsMapRegionId}
                markers={markers}
                markersLoading={markersLoading}
                markerClustering={!withoutOffers}
                disableClusteringAtZoom={MARKER_CLUSTER_DISABLE_ZOOM}
                maxZoom={17}
                zoom={OFFER_MAP_INITIAL_ZOOM}
                districtPolygonTooltip={districtPolygonTooltip}
                panToOfferId={activeOfferId}
                {...(focusOfferLeftInsetPx != null && focusOfferLeftInsetPx > 0 ? {focusOfferLeftInsetPx} : {})}
                height={mapHeight ?? "100%"}
            />
        );
    }
);

LeafletMap.displayName = "LeafletMap";
